/**
 * Reusable hook for intersection-based reveal animations
 * Eliminates duplicate IntersectionObserver patterns across components
 */

import { useEffect, useRef, useState } from 'react';
import { ANIMATION } from '@/config/constants';

interface UseIntersectionRevealOptions {
  /** Threshold for triggering visibility (0-1) */
  threshold?: number;
  /** Whether to only trigger once */
  triggerOnce?: boolean;
  /** Root margin for intersection observer */
  rootMargin?: string;
}

interface UseIntersectionRevealReturn<T extends HTMLElement> {
  /** Ref to attach to the element */
  ref: React.RefObject<T>;
  /** Whether the element is visible */
  isVisible: boolean;
}

/**
 * Hook that tracks element visibility using IntersectionObserver
 * @param options Configuration options for the observer
 * @returns Object containing ref and visibility state
 */
export function useIntersectionReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseIntersectionRevealOptions = {}
): UseIntersectionRevealReturn<T> {
  const {
    threshold = ANIMATION.INTERSECTION_THRESHOLD,
    triggerOnce = true,
    rootMargin = '0px',
  } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (triggerOnce) {
              observer.unobserve(element);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, triggerOnce, rootMargin]);

  return { ref, isVisible };
}

/**
 * Hook for multiple elements with staggered reveal
 * @param count Number of elements to track
 * @param options Configuration options
 * @returns Array of visibility states
 */
export function useMultipleIntersectionReveal(
  count: number,
  options: UseIntersectionRevealOptions = {}
): boolean[] {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(() =>
    Array(count).fill(false)
  );

  const {
    threshold = ANIMATION.INTERSECTION_THRESHOLD,
    triggerOnce = true,
  } = options;

  // This is a simplified version - for complex staggered reveals,
  // use a single container ref with useIntersectionReveal
  useEffect(() => {
    setVisibleItems(Array(count).fill(false));
  }, [count]);

  return visibleItems;
}

export default useIntersectionReveal;
